<script setup lang="ts">
  import AutoSavingForm from '@/components/admin/form/AutoSavingForm.vue'
  import CheckboxField from '@/components/admin/form/CheckboxField.vue'
  import InputField from '@/components/admin/form/InputField.vue'
  import PaginatedTableView, {
    type FetchDataOptions,
    type PaginatedResponse
  } from '@/components/admin/PaginatedTable.vue'
  import DataReplacementPopup from '@/components/app/cases/DataReplacementPopup.vue'
  import FeedbackModal from '@/components/app/cases/feedbackModal.vue'
  import TEMPORARYCaseBaseComponent from '@/components/app/cases/TEMPORARYCaseBaseComponent.vue'
  import TEMPORARYCaseWordDocumentGeneratorTemplateItem from '@/components/app/cases/TEMPORARYCaseWordDocumentGeneratorTemplateItem.vue'
  import BackIcon from '@/components/icons/backIcon.vue'
  import GenericPdfNav from '@/components/pdf/genericPdfNav.vue'
  import SkeletonLoader from '@/components/skeleton/SkeletonLoader.vue'
  import MailEmpty from '@/components/svg/Illustrations/MailEmpty.vue'
  import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
  import Switch from '@/components/ui/switch/Switch.vue'
  import { Table, TableBody, TableCell, TableHeader, TableRow } from '@/components/ui/table'
  import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
  import Image from '@/components/util/Image.vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { debounce } from '@/lib/timing'
  import router from '@/router'
  import { ROUTES } from '@/router/routes'
  import { useUserStore } from '@/shared/stores/userStore'
  import { useLocaleStore } from '@/store/localStore'
  import { CasesService, WordDocumentTemplatesService } from '@/types/generated/services.gen'
  import {
    type CreateWordDocumentRequestRequest,
    type CustomerCase,
    type CustomerWordDocumentTemplate,
    type GenerateDocumentChatRequest,
    ImagePresetEnum,
    type ImageproxyImagePreset,
    type PlaceholderValueRequest,
    type CustomerUser as User,
    WordTemplateStatus
  } from '@/types/generated/types.gen'
  import { VuePDF } from '@tato30/vue-pdf'
  import { usePDFWithCmaps as usePDF } from '@/composables/usePDFWithCmaps'
  import { AlertCircleIcon, ImageIcon, InfoIcon } from 'lucide-vue-next'
  import { computed, onMounted, onUnmounted, provide, ref, watch } from 'vue'
  import { useToast } from '@/components/ui/toast'
  import { stripMarkdownFormatting } from '@/lib/textUtils'
  import { useLawFieldStore } from '@/store/lawFieldStore'
  interface CorrectPlaceholderValueSuggestions {
    placeholder_variable: string
    german_label?: string | null
    english_label?: string | null
    value?: string
    suggestions: Array<{
      suggested_value: string
      score: number
    }>
  }

  interface CorrectBlockValue {
    block_variable: string
    german_label?: string | null
    english_label?: string | null
    is_true: boolean | null
    value?: boolean
  }

  interface CorrectReplaceableVariables {
    placeholders: CorrectPlaceholderValueSuggestions[]
    blocks: CorrectBlockValue[]
  }

  const { t } = useTranslation({
    en: {
      back: 'Back',
      confirmChangesQuestion: 'Do you want to make changes to the document?',
      confirmValues: 'Confirm Values',
      downloadWordFile: 'Download Word File',
      fillWithAI: 'Fill Document with AI',
      checkDocument: 'Check Document',
      selectDocumentTemplate: 'Select Template',
      searchDocumentTemplates: 'Search Document Templates',
      send: 'Send',
      sendQuestionHint: 'Press Enter to send',
      placeholder: 'Placeholder',
      value: 'Value',
      alternativeVariants: 'Alternatives',
      variant: 'Variant',
      scoreExplenation:
        'This score indicates how similar the suggested value is to the actual value in the document. A higher score means that the suggested value is more similar to the actual value.',
      highlightPlaceholders: 'Highlight placeholders',
      generatingPlaceholders: 'Generating placeholders with AI...',
      preparingDocument: 'Preparing document preview...',
      confidence: 'Confidence of AI',
      noTemplateSelected: 'No Template Selected',
      selectTemplateToPreview: 'Please select a template from the list to preview it',
      noTemplatesAvailable: 'No Templates Available',
      noTemplatesMessage: 'There are no approved templates available for this case',
      allLawFields: 'All Law Fields',
      templatesInReview:
        '{count} template currently in review|{count} templates currently in review',
      memories: 'Memories'
    },
    de: {
      back: 'Zurück',
      confirmChangesQuestion: 'Wollen Sie Änderungen am Dokument vornehmen?',
      confirmValues: 'Werte bestätigen',
      downloadWordFile: 'Word-Datei herunterladen',
      fillWithAI: 'Dokument mit KI ausfüllen',
      checkDocument: 'Dokument prüfen',
      selectDocumentTemplate: 'Vorlage auswählen',
      searchDocumentTemplates: 'Dokumentvorlagen suchen',
      send: 'Senden',
      sendQuestionHint: 'Drück Sie Enter zum Senden',
      placeholder: 'Platzhalter',
      value: 'Wert',
      alternativeVariants: 'Alternative',
      variant: 'Variant',
      scoreExplenation:
        'Dieser Score zeigt an, wie similar der vorgeschlagene Wert dem tatsächlichen Wert im Dokument ist. Ein hoeherer Score bedeutet, dass der vorgeschlagene Wert mehr mit dem tatsächlichen Wert im Dokument vergleichbar ist.',
      highlightPlaceholders: 'Platzhalter hervorheben',
      generatingPlaceholders: 'Platzhalter werden mit KI generiert...',
      preparingDocument: 'Dokumentvorschau wird vorbereitet...',
      confidence: 'Vertrauenswürdigkeit der KI',
      noTemplateSelected: 'Keine Vorlage ausgewählt',
      selectTemplateToPreview: 'Bitte wählen Sie eine Vorlage aus der Liste zur Vorschau aus',
      noTemplatesAvailable: 'Keine Vorlagen verfügbar',
      noTemplatesMessage: 'Es sind keine freigegebenen Vorlagen für diesen Fall verfügbar',
      allLawFields: 'Alle Rechtsgebiete',
      templatesInReview:
        '{count} Vorlage befindet sich aktuell in Prüfung. Sie erhalten eine E-Mail, sobald diese verfügbar sind.|{count} Vorlagen befinden sich aktuell in Prüfung. Sie erhalten eine E-Mail, sobald diese verfügbar sind.',
      memories: 'Erinnerungen'
    }
  })

  const userStore = useUserStore()
  const user = computed<User>(() => userStore.user as User)
  const localeStore = useLocaleStore()
  const currentLocale = computed(() => localeStore.getLocale)
  const { getLawFieldOptions } = useLawFieldStore()

  const lawFieldOptions = computed(() => {
    if (!user.value) return []
    return getLawFieldOptions(user.value).map((lawField) => {
      return {
        label: lawField.title,
        value: lawField.value
      }
    })
  })
  const caseData = ref<CustomerCase | null>(null)
  const { toast } = useToast()

  const getLabel = (item: CorrectPlaceholderValueSuggestions | CorrectBlockValue): string => {
    if (currentLocale.value === 'de' && item.german_label) {
      return item.german_label
    }
    if (currentLocale.value === 'en' && item.english_label) {
      return item.english_label
    }
    if (item.german_label) {
      return item.german_label
    }
    if (item.english_label) {
      return item.english_label
    }
    const variableName =
      'placeholder_variable' in item ? item.placeholder_variable : item.block_variable
    return formatPlaceholderName(variableName)
  }

  const formatPlaceholderName = (name: string): string => {
    return name
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
  }

  /**
   * Escape HTML entities to prevent XSS attacks
   * @param text - Raw text that may contain HTML characters
   * @returns HTML-safe escaped string
   */
  const escapeHtml = (text: string): string => {
    const htmlEntities: Record<string, string> = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#x27;',
      '/': '&#x2F;'
    }
    return text.replace(/[&<>"'/]/g, (char) => htmlEntities[char] || char)
  }

  /**
   * Find consistent table structure from processed lines
   * @param tableLines - Array of table lines with cell data
   * @returns Object with columnCount and startIndex, or null if no consistent structure found
   */
  const findConsistentTableStructure = (
    tableLines: { type: 'description' | 'table'; content: string; cells?: string[] }[]
  ): { columnCount: number; startIndex: number } | null => {
    for (let i = 0; i < tableLines.length - 1; i++) {
      const currentCells = tableLines[i].cells?.length || 0
      const nextCells = tableLines[i + 1].cells?.length || 0

      if (currentCells === nextCells && currentCells >= 2) {
        // Check for more consecutive rows with same column count
        let consecutiveRows = 2
        for (let j = i + 2; j < tableLines.length; j++) {
          if ((tableLines[j].cells?.length || 0) === currentCells) {
            consecutiveRows++
          } else {
            break
          }
        }

        if (consecutiveRows >= 2) {
          return {
            columnCount: currentCells,
            startIndex: i
          }
        }
      }
    }
    return null
  }

  // Table styling constants
  const TABLE_STYLES = {
    table: 'border-collapse: collapse; width: 100%;',
    headerCell:
      'border: 1px solid #ccc; padding: 8px; background-color: #f3f4f6; font-weight: bold;',
    dataCell: 'border: 1px solid #ccc; padding: 8px;'
  } as const

  // Text highlighting constants
  const HIGHLIGHT_CONFIG = {
    MAX_SHORT_TEXT_LENGTH: 100, // Max length before splitting into chunks
    MIN_HIGHLIGHT_LENGTH: 10, // Minimum length for a highlight chunk
    WORD_CHUNK_SIZE: 50 // Number of words per chunk for long text
  } as const

  /**
   * Split text into sentences using Intl.Segmenter (modern) or fallback regex
   * Handles common abbreviations like Dr., U.S., etc.
   * @param text - Text to split into sentences
   * @returns Array of sentence strings
   */
  const splitIntoSentences = (text: string): string[] => {
    // Try modern Intl.Segmenter API first (better handling of sentence boundaries)
    // @ts-ignore - Intl.Segmenter is not yet in all TypeScript versions
    if (typeof Intl !== 'undefined' && 'Segmenter' in Intl) {
      try {
        // @ts-ignore - Intl.Segmenter is not yet in all TypeScript versions
        const segmenter = new Intl.Segmenter('de', { granularity: 'sentence' })
        // @ts-ignore
        const segments = segmenter.segment(text)
        // @ts-ignore
        return Array.from(segments)
          .map((segment) => segment.segment.trim())
          .filter((s) => s.length > 0)
      } catch (e) {
        // Fall through to regex fallback
      }
    }

    // Fallback: Enhanced regex that avoids splitting on common abbreviations
    // Common German and English abbreviations
    const abbreviations = [
      'Dr',
      'Prof',
      'etc',
      'z.B',
      'u.a',
      'd.h',
      'bzw',
      'ca',
      'Mr',
      'Mrs',
      'Ms',
      'U.S',
      'U.K',
      'Inc',
      'Ltd',
      'Co'
    ]

    // Replace abbreviations temporarily to protect them
    let protectedText = text
    const placeholders = new Map<string, string>()
    abbreviations.forEach((abbr, index) => {
      const placeholder = `__ABBR_${index}__`
      const patterns = [
        new RegExp(`\\b${abbr}\\.`, 'gi'), // "Dr."
        new RegExp(`\\b${abbr.replace('.', '\\.')}`, 'gi') // "U.S."
      ]
      patterns.forEach((pattern) => {
        protectedText = protectedText.replace(pattern, (match) => {
          placeholders.set(placeholder, match)
          return placeholder
        })
      })
    })

    // Split on sentence boundaries (. ! ?) followed by whitespace and capital letter or end
    const sentences = protectedText
      .split(/([.!?]+)(?=\s+[A-ZÄÖÜ]|\s*$)/)
      .reduce<string[]>((acc, part, i, arr) => {
        if (i % 2 === 0 && part.trim()) {
          const punctuation = arr[i + 1] || ''
          acc.push((part + punctuation).trim())
        }
        return acc
      }, [])

    // Restore abbreviations
    return sentences
      .map((sentence) => {
        let restored = sentence
        placeholders.forEach((original, placeholder) => {
          restored = restored.replace(placeholder, original)
        })
        return restored
      })
      .filter((s) => s.length > 0)
  }

  const selectedTemplate = ref<CustomerWordDocumentTemplate>()
  const selectedTemplateImage = computed<ImageproxyImagePreset>(() => {
    return selectedTemplate.value?.document.images?.find(
      (image) => image.image_preset === ImagePresetEnum.LARGE
    ) as ImageproxyImagePreset
  })

  const step = ref<number>(1)
  const chat = ref<Array<GenerateDocumentChatRequest>>([])

  const savedDocumentUuid = ref<string>('')
  const isSavingDocument = ref<boolean>(false)
  const savedDocumentUrl = ref<string>('')

  const isGeneratingPlaceholders = ref<boolean>(false)
  const replaceableVariables = ref<CorrectReplaceableVariables>(
    {} as unknown as CorrectReplaceableVariables
  )

  const highlightTexts = computed(() => {
    const highlights: string[] = []

    if (replaceableVariables.value?.placeholders) {
      replaceableVariables.value.placeholders.forEach((placeholder) => {
        if (placeholder.value && placeholder.value.trim() !== '') {
          const value = placeholder.value.trim()

          // For very long text, split into smaller chunks for better highlighting
          if (value.length > HIGHLIGHT_CONFIG.MAX_SHORT_TEXT_LENGTH) {
            // Use robust sentence splitting that handles abbreviations
            const sentences = splitIntoSentences(value)

            if (sentences.length > 1) {
              sentences.forEach((sentence) => {
                const trimmed = sentence.trim()
                if (trimmed.length > HIGHLIGHT_CONFIG.MIN_HIGHLIGHT_LENGTH) {
                  // Only highlight meaningful chunks
                  highlights.push(trimmed)
                }
              })
            } else {
              // If no sentence breaks, split by words in chunks
              const words = value.split(/\s+/)
              for (let i = 0; i < words.length; i += HIGHLIGHT_CONFIG.WORD_CHUNK_SIZE) {
                const chunk = words.slice(i, i + HIGHLIGHT_CONFIG.WORD_CHUNK_SIZE).join(' ')
                if (chunk.trim()) {
                  highlights.push(chunk.trim())
                }
              }
            }
          } else {
            highlights.push(value)
          }
        } else {
          highlights.push(`{{${placeholder.placeholder_variable}}}`)
        }
      })
    }

    if (replaceableVariables.value?.blocks) {
      // No highlighting needed for blocks
    }

    return highlights
  })

  const arrayBuffer = ref<Uint8Array | null>(null)
  const { pdf, pages } = usePDF(arrayBuffer)
  const pdfLoaded = ref<boolean>(false)
  const currentPage = ref<number>(1)
  const createPdf = ref<boolean>(true)
  const pdfUpdateTimeout = ref<number | null>(null)

  const showFeedbackModal = ref(false)

  const templatesInReview = ref(0)

  const updateTemplatesInReview = async () => {
    try {
      const result = await WordDocumentTemplatesService.wordDocumentTemplatesList({
        status: WordTemplateStatus.IN_REVIEW
      })
      templatesInReview.value = result.count || 0
    } catch (error) {
      console.error('Error fetching templates in review:', error)
    }
  }

  const findPlaceholdersWithAI = async () => {
    if (!selectedTemplate.value) {
      return
    }

    isGeneratingPlaceholders.value = true
    try {
      const response = await CasesService.casesFindWordDocumentTemplatePlaceholderValuesCreate({
        uuid: router.currentRoute.value.params.uuid.toString(),
        requestBody: {
          word_document_template_uuid: selectedTemplate.value!.uuid as string
        }
      })

      replaceableVariables.value =
        response.replaceable_variables as unknown as CorrectReplaceableVariables

      // Initialize placeholder values with suggestions
      replaceableVariables.value.placeholders.forEach((placeholder) => {
        if (placeholder.suggestions.length > 0) {
          placeholder.value = placeholder.suggestions[0].suggested_value
        } else {
          placeholder.value = ''
        }
      })

      // Initialize block values with their is_true property
      if (replaceableVariables.value.blocks && replaceableVariables.value.blocks.length > 0) {
        replaceableVariables.value.blocks.forEach((block) => {
          block.value = block.is_true === true
        })
      }

      await fetchPreviewPdf()

      console.log('Successfully generated placeholders:', replaceableVariables.value)
    } catch (error) {
      console.error('Error generating placeholders:', error)
    } finally {
      isGeneratingPlaceholders.value = false
    }
  }

  function getSelectedSuggestion(placeholder: CorrectPlaceholderValueSuggestions) {
    return (
      placeholder.suggestions.find(
        (suggestion) => suggestion.suggested_value === placeholder.value
      ) || placeholder.suggestions[0]
    )
  }

  const navigateToStepOne = async () => {
    step.value = 1
    chat.value = []
    isGeneratingPlaceholders.value = false
  }

  const navigateToStepTwo = async () => {
    if (!selectedTemplate.value?.docx_template) {
      return
    }
    // Reset placeholders and related state before moving to step 2
    replaceableVariables.value = {} as unknown as CorrectReplaceableVariables
    arrayBuffer.value = null
    pdfLoaded.value = false

    // Set step and template name
    step.value = 2
    createDocumentRequest.value.name = selectedTemplate.value!.name

    // Generate new placeholders
    await findPlaceholdersWithAI()

    // Generate PDF preview after placeholders are loaded
    await fetchPreviewPdf()
  }

  const handleBlockChange = (value: boolean, block: CorrectBlockValue) => {
    replaceableVariables.value.blocks.find(
      (b) => b.block_variable === block.block_variable
    )!.value = value
    fetchPreviewPdf()
  }

  const handleFeedbackClose = () => {
    showFeedbackModal.value = false
    navigateToStepOne()
  }

  const handleFeedbackSubmitted = (feedbackData: any) => {
    showFeedbackModal.value = false
    // Navigate back to case detail
    router.push(
      ROUTES.APP.CASES.CASE_DETAIL.LAW_SPECIFIC_DATA.replace(':uuid', caseData.value?.uuid ?? '')
    )
  }

  const formatTableForWord = (content: string): string => {
    // Check if content looks like a table
    const lines = content.split('\n').filter((line) => line.trim())
    const hasTableStructure =
      lines.length > 2 && lines.some((line) => line.includes('|') || line.includes('\t'))

    if (hasTableStructure) {
      // Find actual table rows and handle mixed description+header lines
      const processedLines: { type: 'description' | 'table'; content: string; cells?: string[] }[] =
        []

      lines.forEach((line, index) => {
        if (line.includes('|') || line.includes('\t')) {
          // Split the line by separators and strip markdown from each cell
          const parts = line
            .split(/\s*\|\s*|\t+/)
            .map((part) => stripMarkdownFormatting(part.trim()))

          // Check if the first part looks like descriptive text (long text ending with colon)
          if (parts.length >= 3 && parts[0].length > 30 && parts[0].endsWith(':')) {
            // This is likely description + table headers on same line
            processedLines.push({ type: 'description', content: parts[0] })

            // The remaining parts are likely table headers
            const headerCells = parts.slice(1).filter((cell) => cell && !cell.match(/^-+$/))
            if (headerCells.length >= 2) {
              processedLines.push({ type: 'table', content: '', cells: headerCells })
            }
          } else {
            // Regular table row
            const cells = parts.filter((cell) => cell && !cell.match(/^-+$/))
            if (cells.length >= 2) {
              processedLines.push({ type: 'table', content: '', cells })
            }
          }
        } else {
          // Regular description line - strip markdown formatting
          processedLines.push({ type: 'description', content: stripMarkdownFormatting(line) })
        }
      })

      // Find consistent table structure
      const tableLines = processedLines.filter((line) => line.type === 'table')
      if (tableLines.length < 2) return content

      const tableStructure = findConsistentTableStructure(tableLines)
      if (!tableStructure) return content

      const { columnCount: consistentColumnCount } = tableStructure

      // Separate description from table
      const descriptionLines: string[] = []
      const validTableRows: string[][] = []

      // Add all description lines that come before the table
      let foundTableStart = false
      for (const line of processedLines) {
        if (line.type === 'table' && (line.cells?.length || 0) === consistentColumnCount) {
          foundTableStart = true
          validTableRows.push(line.cells!)
        } else if (!foundTableStart) {
          descriptionLines.push(line.content)
        }
      }

      if (validTableRows.length >= 2) {
        // Create HTML table with escaped cell values
        const headerRow = validTableRows[0]
        const dataRows = validTableRows.slice(1)

        let htmlTable = `<table border="1" style="${TABLE_STYLES.table}">\n`

        // Add header row with escaped values
        htmlTable += '<thead><tr>'
        headerRow.forEach((cell) => {
          const escapedCell = escapeHtml(cell)
          htmlTable += `<th style="${TABLE_STYLES.headerCell}">${escapedCell}</th>`
        })
        htmlTable += '</tr></thead>\n'

        // Add data rows with escaped values
        htmlTable += '<tbody>'
        dataRows.forEach((row) => {
          htmlTable += '<tr>'
          row.forEach((cell) => {
            const escapedCell = escapeHtml(cell)
            htmlTable += `<td style="${TABLE_STYLES.dataCell}">${escapedCell}</td>`
          })
          htmlTable += '</tr>'
        })
        htmlTable += '</tbody></table>'

        // Combine description with table (escape description too)
        let result = ''
        if (descriptionLines.length > 0) {
          const escapedDescription = descriptionLines.map(escapeHtml).join('<br>')
          result = escapedDescription + '<br><br>' + htmlTable
        } else {
          result = htmlTable
        }

        return result
      }
    }

    // If not a table, still strip markdown formatting from the content
    return stripMarkdownFormatting(content)
  }

  const handleDataSelection = (
    placeholder: CorrectPlaceholderValueSuggestions,
    selectedValue: string
  ) => {
    // Format table content for better Word rendering
    const formattedValue = formatTableForWord(selectedValue)
    placeholder.value = formattedValue

    // Force deep reactivity update by creating new objects
    replaceableVariables.value = {
      ...replaceableVariables.value,
      placeholders:
        replaceableVariables.value.placeholders?.map((p) =>
          p.placeholder_variable === placeholder.placeholder_variable
            ? { ...p, value: formattedValue }
            : { ...p }
        ) || [],
      blocks: replaceableVariables.value.blocks?.map((b) => ({ ...b })) || []
    }

    // Trigger PDF update immediately (no debounce for snippet selection)
    handleSave()
  }

  const setPage = (page: number) => {
    currentPage.value = page
  }

  /**
   * Process replaceable variables and return an array of placeholder value requests
   * @param useEmptyPlaceholders Whether to use empty placeholders with {{...}} format
   * @returns Array of PlaceholderValueRequest objects
   */
  const processReplaceableVariables = (useEmptyPlaceholders = true): PlaceholderValueRequest[] => {
    const placeholderValuesRequest: PlaceholderValueRequest[] = []

    // Process all replaceable variables
    if (replaceableVariables.value) {
      // Add placeholder values from replaceable variables
      if (replaceableVariables.value.placeholders) {
        replaceableVariables.value.placeholders.forEach((placeholder) => {
          const value = placeholder.value || ''

          // Only add non-empty values when useEmptyPlaceholders is false
          if (!useEmptyPlaceholders && (!value || value.trim() === '')) {
            return
          }

          placeholderValuesRequest.push({
            placeholder: placeholder.placeholder_variable,
            value:
              value !== ''
                ? value
                : useEmptyPlaceholders
                  ? `{{${placeholder.placeholder_variable}}}`
                  : ''
          })
        })
      }

      // Add block values from replaceable variables
      if (replaceableVariables.value.blocks && Array.isArray(replaceableVariables.value.blocks)) {
        replaceableVariables.value.blocks.forEach((block) => {
          // Add null checks to prevent TypeError
          if (
            block &&
            typeof block === 'object' &&
            block.block_variable &&
            block.value !== undefined
          ) {
            placeholderValuesRequest.push({
              placeholder: block.block_variable,
              value: String(block.value)
            })
          }
        })
      }
    }

    return placeholderValuesRequest
  }

  const fetchPreviewPdf = async () => {
    if (!selectedTemplate.value || !selectedTemplate.value.uuid || isGeneratingPlaceholders.value) {
      console.log(
        'Skipping PDF preview generation: Template not selected or placeholders still loading'
      )
      return
    }

    // Reset PDF loaded state
    pdfLoaded.value = false

    // Get placeholder values with empty placeholders formatted as {{placeholder_variable}}
    const placeholderValuesRequest = processReplaceableVariables(true)

    try {
      const response = await CasesService.casesCreateWordDocumentPreviewBlobCreate({
        uuid: router.currentRoute.value.params.uuid.toString(),
        requestBody: {
          word_document_template_uuid: selectedTemplate.value!.uuid,
          placeholder_values: placeholderValuesRequest
        }
      })

      const buffer = await response.arrayBuffer()
      arrayBuffer.value = new Uint8Array(buffer)
    } catch (error: any) {
      console.error('Failed to load PDF:', error)

      // Handle Jinja2 syntax errors specifically
      if (error.status === 400 && error.body?.error && error.body?.blocks) {
        const blocksList = error.body.blocks.map((block: any) => `- ${block}`).join('\n')
        toast({
          title: 'Jinja2 Syntax Error detected in document.',
          description: `Please check blocks:\n${blocksList}`,
          variant: 'destructive',
          duration: 10000
        })
      } else {
        toast({
          title: 'PDF Preview Error',
          description: error.body || 'Failed to generate PDF preview',
          variant: 'destructive',
          duration: 5000
        })
      }
    }
  }

  const handleSave = async () => {
    if (!isGeneratingPlaceholders.value) {
      await fetchPreviewPdf()
    }
  }

  const debouncedSave = debounce(() => handleSave(), 500)

  const saveDocument = async () => {
    if (!selectedTemplate.value?.uuid) return
    isSavingDocument.value = true

    // Get placeholder values without empty placeholders
    const placeholderValuesRequest = processReplaceableVariables(false)

    const response = await CasesService.casesCreateWordDocumentFromPlaceholderValuesCreate({
      uuid: router.currentRoute.value.params.uuid.toString(),
      requestBody: {
        word_document_template_uuid: selectedTemplate.value?.uuid,
        name: selectedTemplate.value?.name,
        placeholder_values: placeholderValuesRequest,
        create_pdf: createPdf.value
      }
    })
    isSavingDocument.value = false
    savedDocumentUrl.value = response.url
    savedDocumentUuid.value = response.document_uuid

    // Trigger a programmatic download
    const link = document.createElement('a') // Create a link element
    link.href = savedDocumentUrl.value // Set the href to the file URL
    link.download = `${createDocumentRequest.value.name}.docx` // Set the desired file name (optional)
    document.body.appendChild(link) // Append the element to the DOM
    link.click() // Trigger the click event (this starts the download)
    document.body.removeChild(link) // Remove it from the DOM afterward
    showFeedbackModal.value = true
  }

  const createDocumentRequest = ref<CreateWordDocumentRequestRequest>({
    name: '',
    html: '',
    create_pdf: true
  })

  const onTemplateSelect = (template: CustomerWordDocumentTemplate) => {
    selectedTemplate.value = template
  }

  const onPdfLoaded = (eventData: any) => {
    pdfLoaded.value = true
  }

  const onHighlightApplied = (payload: any) => {
    /**
     * This function is called when the highlight is applied to the document, it then adds tooltip to the highlights of replaced
     * placeholders to show the placeholder variable name, and replaces highlights colors for the non replaced placeholders.
     *
     * @param payload - The payload object containing the matches and textDivs
     */
    // Defer execution to avoid race conditions with PDF rendering
    setTimeout(() => {
      if (!payload) return

      // Safely extract matches and textDivs
      const matches = payload?.matches || []
      const textDivs = payload?.textDivs || []

      if (!Array.isArray(matches) || matches.length === 0 || !Array.isArray(textDivs)) return

      // Create map of placeholder values to their variable names
      const placeholderMap = new Map()
      if (replaceableVariables.value?.placeholders) {
        replaceableVariables.value.placeholders.forEach((placeholder) => {
          if (placeholder.value && placeholder.value.trim() !== '') {
            placeholderMap.set(placeholder.value, placeholder.placeholder_variable)
          }
        })
      }

      // Process each match one by one
      for (let i = 0; i < matches.length; i++) {
        const match = matches[i]
        if (!match || !match.str || typeof match.start?.idx !== 'number') continue

        const text = match.str
        if (!text) continue

        if (match.start.idx < 0 || match.start.idx >= textDivs.length) continue

        const div = textDivs[match.start.idx]
        if (!div || !div.querySelectorAll) continue

        const placeholderMatch = text.match(/^\{\{(.*)\}\}$/)
        const isUnreplacedPlaceholder = !!placeholderMatch
        const isReplacedPlaceholder = placeholderMap.has(text)
        const placeholderVariable = isReplacedPlaceholder ? placeholderMap.get(text) : ''

        let highlightSpans
        try {
          highlightSpans = div.querySelectorAll('span.highlight')
        } catch (e) {
          console.error('Error querying highlight spans:', e)
          continue
        }

        if (!highlightSpans || highlightSpans.length === 0) continue

        // Process each span
        for (let j = 0; j < highlightSpans.length; j++) {
          const span = highlightSpans[j]
          if (!span || !span.classList) continue

          try {
            if (isUnreplacedPlaceholder) {
              span.classList.add('highlight-placeholder')
              span.classList.remove('highlight')
            } else if (isReplacedPlaceholder) {
              span.classList.add('highlight-replaced')
              span.classList.remove('highlight')

              span.setAttribute('data-tooltip', placeholderVariable)
              span.classList.add('tooltip-trigger')
            }
          } catch (error) {
            console.error('Error applying highlight styles:', error)
          }
        }
      }
    }, 50) // Small delay to ensure PDF is fully rendered
  }

  const fetchCase = async () => {
    caseData.value = (await CasesService.casesRetrieve({
      uuid: router.currentRoute.value.params.uuid.toString()
    })) as CustomerCase
  }

  // Provide caseData to child components (like ChatMemoriesDialog)
  provide('caseData', caseData)

  const fetchTemplates = async (options?: FetchDataOptions | undefined) => {
    try {
      const limit = options?.limit
      const offset = options?.offset
      const search = options?.search
      const filters = options?.filters
      const lawField = filters?.find((f) => f.key === 'law_field')?.value
      const status = WordTemplateStatus.APPROVED

      const params = {
        limit,
        offset,
        search,
        lawField,
        status
      }

      const result = (await WordDocumentTemplatesService.wordDocumentTemplatesList(
        params
      )) as PaginatedResponse<CustomerWordDocumentTemplate>

      if (!selectedTemplate.value && offset === 0 && result.results && result.results.length > 0) {
        const approvedTemplates = result.results.filter(
          (template) => template.status === WordTemplateStatus.APPROVED
        )
        if (approvedTemplates.length > 0) {
          selectedTemplate.value = approvedTemplates[0]
        }
      }
      return result
    } catch (error) {
      console.error('Error fetching templates:', error)
      return { results: [], count: 0, next: null, previous: null }
    }
  }

  watch(pdf, (newPdf) => {
    if (newPdf) {
      console.log('PDF object is available')
      pdfLoaded.value = true
    }
  })

  // Watch for placeholder generation completion to automatically generate PDF preview
  watch(isGeneratingPlaceholders, (isGenerating, prevIsGenerating) => {
    if (prevIsGenerating === true && isGenerating === false && step.value === 2) {
      console.log('Placeholder generation completed, generating PDF preview')
      fetchPreviewPdf()
    }
  })

  onMounted(async () => {
    if (!user.value) {
      await userStore.fetchUser()
    }
    await Promise.all([fetchCase(), updateTemplatesInReview()])
  })

  onUnmounted(() => {
    // Clean up any pending timeouts when component is unmounted
    if (pdfUpdateTimeout.value) {
      clearTimeout(pdfUpdateTimeout.value)
    }
  })

  // Watch for template changes and reset state if we're on step 2
  watch(selectedTemplate, (newTemplate, oldTemplate) => {
    if (newTemplate?.uuid !== oldTemplate?.uuid && step.value === 2) {
      console.log('Template changed, resetting state')
      // If we're on step 2 and the template changes, go back to step 1
      // This forces a proper reset when the user selects a new template
      navigateToStepOne()
    }
  })
</script>

<template>
  <TEMPORARYCaseBaseComponent :case-data="caseData">
    <div>
      <div class="mt-4">
        <router-link
          v-if="step === 1 || step === 3"
          :to="`${ROUTES.APP.CASES.CASE_DETAIL.LAW_SPECIFIC_DATA.replace(':uuid', caseData?.uuid ?? '')}`"
          class="inline-flex items-center gap-x-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm
            font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500
            disabled:pointer-events-none disabled:opacity-50"
        >
          <BackIcon class="h-5 w-5" />
          {{ t('back') }}
        </router-link>
        <button
          v-if="step === 2"
          class="inline-flex items-center gap-x-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm
            font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500
            disabled:pointer-events-none disabled:opacity-50"
          @click="navigateToStepOne"
        >
          <BackIcon class="h-5 w-5" />
          {{ t('back') }}
        </button>
      </div>

      <!-- Document upload banner - Right after back button -->
      <div
        v-if="step == 1"
        class="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm"
      >
        <p>
          Ist Ihr gewünschtes Dokument noch nicht dabei? Klicken Sie hier auf
          <router-link
            :to="ROUTES.APP.SETTINGS.AI.DOCUMENT_GENERATION_TEMPLATES"
            class="text-purple-600 underline hover:text-purple-800"
          >
            KI-Einstellungen › Dokumentenvorlagen
          </router-link>
          , laden Sie Ihr Word-Dokument hoch und nach einer kurzen Qualitätskontrolle durch unser
          KI-Team wird es im Generator bereitgestellt.
        </p>
      </div>
      <div
        v-if="step == 1 || step === 2 || step === 3"
        class="mt-4 rounded-xl border border-gray-200 bg-white p-4"
      >
        <div class="flex items-center gap-5">
          <!-- Left Section -->
          <div class="w-3/6">
            <div class="flex items-center gap-x-4">
              <!-- Step 1 -->
              <div
                class="text"
                :class="step == 1 ? 'font-bold' : 'text-gray-600'"
              >
                <span
                  class="mr-2 inline-flex items-center gap-x-1.5 rounded-lg px-3 py-1.5 text-xs font-medium"
                  :class="step == 1 ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-600'"
                >
                  1
                </span>
                {{ t('selectDocumentTemplate') }}
              </div>
              <!-- Step 2 -->
              <div
                class="text"
                :class="step == 2 ? 'font-bold' : 'text-gray-600'"
              >
                <span
                  class="mr-2 inline-flex items-center gap-x-1.5 rounded-lg px-3 py-1.5 text-xs font-medium"
                  :class="step == 2 ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-600'"
                >
                  2
                </span>
                {{ t('confirmValues') }}
              </div>
            </div>
          </div>

          <!-- Right Section -->
          <div class="w-3/6">
            <div class="flex items-center justify-end gap-x-4">
              <!-- Memory Button for Step 1 -->
              <!-- <button
                v-if="step === 1"
                class="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-4
                  py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus-visible:outline-none
                  focus-visible:ring-2 focus-visible:ring-offset-2"
                @click="showMemoriesDialog = true"
              >
                <Brain class="h-4 w-4" />
                {{ t('memories') }}
              </button> -->
              <!-- Button for Step 1 -->
              <button
                v-if="step === 1"
                class="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-purple-600 px-4 py-2 text-sm
                  font-medium text-white transition-colors hover:bg-purple-700 focus-visible:outline-none
                  focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50"
                :disabled="!selectedTemplate?.docx_template"
                @click="navigateToStepTwo"
              >
                {{ t('fillWithAI') }}
              </button>
              <CheckboxField
                v-if="step === 2"
                v-model="createPdf"
                name="create_pdf"
                label="PDF in Anfrage speichern"
                :disabled="isGeneratingPlaceholders || isSavingDocument"
              />
              <button
                v-if="step === 2"
                :disabled="isGeneratingPlaceholders || isSavingDocument"
                class="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-purple-600 px-4 py-2 text-sm
                  font-medium text-white transition-colors hover:bg-purple-700 focus-visible:outline-none
                  focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50"
                @click="saveDocument"
              >
                {{ t('downloadWordFile') }}
              </button>
            </div>
          </div>
        </div>

        <div
          v-if="step === 1"
          class="pt-4"
        >
          <div class="flex gap-5">
            <div class="w-5/12 border-r-2 pr-4">
              <div>
                <!-- Templates in review info box -->
                <div
                  v-if="templatesInReview > 0"
                  class="mb-4 flex items-center rounded-lg bg-blue-50 px-4 py-3 text-sm text-blue-800"
                >
                  <div class="mr-2 flex-shrink-0">
                    <AlertCircleIcon class="h-5 w-5" />
                  </div>
                  <div>
                    {{ t('templatesInReview', { count: templatesInReview }) }}
                  </div>
                </div>

                <!-- Paginated templates table -->
                <PaginatedTableView
                  searchable-fields="name"
                  :fetch-data="fetchTemplates"
                  :filterable-fields="[
                    {
                      key: 'law_field',
                      label: 'Rechtsgebiet',
                      type: 'select',
                      options: lawFieldOptions
                    }
                  ]"
                >
                  <template #table-row="{ items }">
                    <TableRow
                      v-for="template in items.filter(
                        (t) => t.status === WordTemplateStatus.APPROVED
                      )"
                      :key="template.uuid"
                      :class="{
                        'bg-purple-50 hover:bg-purple-100':
                          selectedTemplate && selectedTemplate.uuid === template.uuid
                      }"
                      class="cursor-pointer"
                      @click="onTemplateSelect(template)"
                    >
                      <TableCell>
                        <TEMPORARYCaseWordDocumentGeneratorTemplateItem
                          :template="template"
                          :isSelected="selectedTemplate && selectedTemplate.uuid === template.uuid"
                          @select="onTemplateSelect"
                        />
                      </TableCell>
                    </TableRow>
                  </template>
                </PaginatedTableView>
              </div>
            </div>

            <div class="w-7/12">
              <div class="h-full rounded-xl border border-gray-200">
                <template v-if="selectedTemplateImage">
                  <Image
                    class="p-1"
                    :src="selectedTemplateImage.url"
                    :height="'800px'"
                  />
                </template>
                <div
                  v-else
                  class="flex h-[600px] flex-col items-center justify-center p-8 text-center"
                >
                  <MailEmpty class="mb-6 h-48 w-48" />
                  <h3 class="text-lg font-semibold text-gray-700">{{ t('noTemplateSelected') }}</h3>
                  <p class="mt-2 text-sm text-gray-500">{{ t('selectTemplateToPreview') }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="step === 2"
          class="flex gap-5 pt-4"
        >
          <!-- Global loading overlay -->
          <div
            v-if="isGeneratingPlaceholders"
            class="absolute inset-0 z-10 flex items-center justify-center"
          >
            <div class="flex flex-col items-center space-y-4 rounded-lg bg-white/80 p-8 shadow-lg">
              <div class="h-16 w-16 animate-spin rounded-full border-b-2 border-purple-700"></div>
              <p class="text-xl font-medium text-gray-700">{{ t('generatingPlaceholders') }}</p>
            </div>
          </div>
          <div
            class="max-h-[1100px] w-5/12 overflow-auto pr-2"
            :class="{ 'opacity-0': isGeneratingPlaceholders }"
          >
            <AutoSavingForm
              :debounce-delay="500"
              :throttle-interval="800"
              @save="debouncedSave"
            >
              <div>
                <Table class="h-full w-full">
                  <TableHeader>
                    <TableRow>
                      <TableCell class="w-2/5">{{ t('placeholder') }}</TableCell>
                      <TableCell class="w-3/5">{{ t('value') }}</TableCell>
                    </TableRow>
                  </TableHeader>
                  <TableBody
                    v-if="
                      replaceableVariables?.placeholders?.length > 0 ||
                      replaceableVariables?.blocks?.length > 0
                    "
                  >
                    <!-- Blocks Rows -->
                    <TableRow
                      v-for="block in replaceableVariables?.blocks"
                      :key="block.block_variable"
                      class="border-b"
                    >
                      <TableCell class="py-4 pr-2">
                        <div class="flex items-start gap-2">
                          <span class="flex-1 break-words font-medium text-gray-900">
                            {{ getLabel(block) }}
                          </span>
                          <TooltipProvider :delay-duration="0">
                            <Tooltip>
                              <TooltipTrigger as-child>
                                <InfoIcon
                                  class="mt-0.5 h-4 w-4 flex-shrink-0 cursor-pointer text-gray-400"
                                />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p class="text-sm">{{ block.block_variable }}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </TableCell>
                      <TableCell class="py-4">
                        <div class="flex items-center gap-3">
                          <Switch
                            :id="`block_${block.block_variable}`"
                            v-model:checked="block.value"
                            class="data-[state=checked]:bg-purple-500 data-[state=unchecked]:bg-gray-200"
                            @update:checked="handleBlockChange($event, block)"
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                    <!-- Placeholders Rows -->
                    <TableRow
                      v-for="placeholder in replaceableVariables?.placeholders"
                      :key="placeholder.placeholder_variable"
                      class="border-b"
                    >
                      <TableCell class="py-4 pr-2">
                        <div class="flex items-start gap-2">
                          <span class="flex-1 break-words font-medium text-gray-900">
                            {{ getLabel(placeholder) }}
                          </span>
                          <TooltipProvider :delay-duration="0">
                            <Tooltip>
                              <TooltipTrigger as-child>
                                <InfoIcon
                                  class="mt-0.5 h-4 w-4 flex-shrink-0 cursor-pointer text-gray-400"
                                />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p class="text-sm">{{ placeholder.placeholder_variable }}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </TableCell>
                      <TableCell class="py-4">
                        <div class="flex items-center gap-2">
                          <!-- Input field with confidence indicator inside -->
                          <div class="relative flex-1">
                            <div class="truncate-input w-full">
                              <InputField
                                :id="`placeholder_${placeholder.placeholder_variable}`"
                                v-model="placeholder.value"
                                :name="`placeholder_${placeholder.placeholder_variable}`"
                                type="text"
                                :disabled="isGeneratingPlaceholders"
                              />
                            </div>
                            <!-- Confidence indicator positioned absolutely inside input -->
                            <div
                              v-if="getSelectedSuggestion(placeholder)"
                              class="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1"
                            >
                              <!-- Confidence percentage with tooltip -->
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger>
                                    <span
                                      :class="{
                                        'bg-blue-100 text-blue-800':
                                          getSelectedSuggestion(placeholder).score >= 80,
                                        'bg-yellow-100 text-yellow-800':
                                          getSelectedSuggestion(placeholder).score >= 50 &&
                                          getSelectedSuggestion(placeholder).score < 80,
                                        'bg-red-100 text-red-800':
                                          getSelectedSuggestion(placeholder).score < 50
                                      }"
                                      class="flex cursor-pointer items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium"
                                    >
                                      {{ Math.round(getSelectedSuggestion(placeholder).score) }}%
                                      <!-- Warning icon for low confidence -->
                                      <AlertCircleIcon
                                        v-if="getSelectedSuggestion(placeholder).score < 50"
                                        class="h-4 w-4 text-red-500"
                                      />
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent class="bg-black p-2 text-sm text-white shadow">
                                    {{ t('confidence') }}
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                          </div>

                          <!-- Data replacement popup -->
                          <DataReplacementPopup
                            :placeholder-label="getLabel(placeholder)"
                            :case-data="caseData"
                            @select-data="(value) => handleDataSelection(placeholder, value)"
                          />

                          <!-- Suggestions popover with radio buttons -->
                          <Popover v-if="placeholder.suggestions.length > 1">
                            <PopoverTrigger
                              class="text-md flex items-center gap-1 px-2 font-medium text-purple-600 underline hover:text-purple-800"
                            >
                              +{{ placeholder.suggestions.length - 1 }}
                            </PopoverTrigger>
                            <PopoverContent class="w-[480px]">
                              <div class="border-b p-2">
                                <h4 class="font-medium">Available suggestions</h4>
                              </div>
                              <div class="max-h-[300px] overflow-auto">
                                <div
                                  v-for="(suggestion, index) in placeholder.suggestions"
                                  :key="index"
                                  class="flex w-full cursor-pointer items-center justify-between border-b p-3 hover:bg-gray-50"
                                  @click="placeholder.value = suggestion.suggested_value"
                                >
                                  <div class="flex items-center gap-3">
                                    <input
                                      type="radio"
                                      :name="`suggestion-${placeholder.placeholder_variable}`"
                                      :checked="placeholder.value === suggestion.suggested_value"
                                      class="h-4 w-4 text-purple-600 focus:ring-purple-500"
                                    />
                                    <span>{{ suggestion.suggested_value }}</span>
                                  </div>
                                  <span
                                    :class="{
                                      'bg-blue-100 text-blue-800': suggestion.score >= 80,
                                      'bg-yellow-100 text-yellow-800':
                                        suggestion.score >= 50 && suggestion.score < 80,
                                      'bg-red-100 text-red-800': suggestion.score < 50
                                    }"
                                    class="rounded-full px-2 py-0.5 text-xs font-medium"
                                  >
                                    {{ Math.round(suggestion.score) }}%
                                  </span>
                                </div>
                              </div>
                            </PopoverContent>
                          </Popover>
                        </div>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </AutoSavingForm>
          </div>
          <div
            class="w-7/12"
            :class="{ 'opacity-0': isGeneratingPlaceholders }"
          >
            <!-- PDF viewer container -->
            <div class="w-full flex-1 overflow-auto rounded-lg border border-gray-200">
              <div class="relative h-full flex-1">
                <div v-if="pdfLoaded">
                  <div class="flex h-full items-center justify-center">
                    <div class="pdf-container">
                      <VuePDF
                        ref="vuePDFRef"
                        :pdf="pdf"
                        class="pdf"
                        on-layer
                        text-layer
                        :scale="1.2"
                        :page="currentPage || 1"
                        :highlight-text="highlightTexts"
                        :highlight-options="{
                          completeWords: false,
                          ignoreCase: false
                        }"
                        @loaded="onPdfLoaded"
                        @text-loaded="onPdfLoaded"
                        @highlight="onHighlightApplied"
                      />
                    </div>
                  </div>
                </div>
                <div
                  v-else
                  class="overflow-hidden"
                >
                  <div>
                    <SkeletonLoader
                      :width="'100%'"
                      :height="'100%'"
                      class="flex min-h-[1000px] items-center justify-center bg-gray-100"
                    >
                      <ImageIcon class="h-6 w-6 text-gray-400" />
                    </SkeletonLoader>
                  </div>
                </div>
              </div>
            </div>
            <div class="mt-3 flex items-center justify-between">
              <GenericPdfNav
                :current-page="currentPage || 1"
                :total-pages="pages || 1"
                @update-page="setPage"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <FeedbackModal
      v-if="showFeedbackModal"
      :document-uuid="savedDocumentUuid"
      :template-uuid="selectedTemplate?.uuid"
      @close="handleFeedbackClose"
      @submitted="handleFeedbackSubmitted"
    />
  </TEMPORARYCaseBaseComponent>
</template>

<style scoped>
  /* Custom styling for highlighted text in PDF */
  :deep(.highlight) {
    background-color: rgba(69, 154, 252, 0.3); /* Blue highlight with transparency */
    border: 2px dashed rgba(69, 154, 252, 0.3); /* Light blue dashed border */
    border-radius: 2px;
    padding: 0;
    margin: -2 -1;
    box-shadow: 0 0 4px rgba(69, 154, 252, 0.5);
  }

  /* Custom styling for unreplaced placeholder highlights ({{...}} format) */
  :deep(.highlight-placeholder) {
    background-color: rgba(255, 165, 0, 0.3); /* Light orange highlight with transparency */
    border: 2px dashed rgba(255, 165, 0, 0.3); /* Light orange dashed border */
    border-radius: 2px;
    margin: -2px -1px;
    padding: 0;
    box-shadow: 0 0 4px rgba(255, 165, 0, 0.5);
    position: relative;
  }

  /* Custom styling for replaced placeholder highlights (with actual values) */
  :deep(.highlight-replaced) {
    background-color: rgba(69, 154, 252, 0.3); /* Blue highlight with transparency */
    border: 2px dashed rgba(69, 154, 252, 0.3); /* Light blue dashed border */
    border-radius: 2px;
    margin: -2px -1px;
    padding: 0;
    box-shadow: 0 0 4px rgba(69, 154, 252, 0.5);
    position: relative;
    cursor: pointer;
  }

  /* Tooltip styling for highlighted placeholders */
  :deep(.tooltip-trigger) {
    position: relative;
  }

  :deep(.tooltip-trigger::after) {
    content: attr(data-tooltip);
    position: absolute;
    bottom: 125%;
    left: 50%;
    transform: translateX(-50%);
    background-color: rgba(0, 0, 0, 0.9);
    color: white;
    padding: 6px 10px;
    border-radius: 6px;
    font-family:
      'Inter',
      -apple-system,
      BlinkMacSystemFont,
      'Segoe UI',
      Roboto,
      sans-serif;
    font-size: 12px;
    font-weight: 400;
    white-space: nowrap;
    opacity: 0;
    visibility: hidden;
    transition:
      opacity 0.2s,
      visibility 0.2s;
    z-index: 100;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }

  :deep(.tooltip-trigger:hover::after) {
    opacity: 1;
    visibility: visible;
  }

  .pdf-container {
    max-width: 100%;
    overflow: auto;
  }

  .truncate-input :deep(input) {
    text-overflow: ellipsis !important;
    overflow: hidden !important;
    white-space: nowrap !important;
    padding-right: 3.25rem !important; /* equivalent to Tailwind's pr-14 */
  }
</style>
